import React from 'react'
import { useIntl } from 'react-intl'
import { Box, MenuItem, Select } from '@mui/material'
import { capitalize } from '../../utils/functions'
import { ui } from '../../utils/uiTokens'

interface Option {
    id: number | string
    name?: string
    title?: string
    titleKz?: string
    titleRu?: string
    firstName?: string
    lastName?: string
}

interface Props {
    label: string
    placeholder: string
    value?: number | string
    options: Option[]
    disabled?: boolean
    onChange: (value: string) => void
}

export const filterLabel = {
    fontSize: '12px',
    fontWeight: 600,
    letterSpacing: '0.04em',
    textTransform: 'uppercase' as const,
    color: ui.muted,
    marginBottom: '6px',
}

export const filterField = {
    width: '100%',
    borderRadius: ui.radius,
    fontSize: '13.5px',
    fontWeight: 600,
    color: ui.ink,
    backgroundColor: ui.card,
    '& .MuiOutlinedInput-notchedOutline': { borderColor: ui.border },
    '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: ui.pink },
    '&.Mui-focused .MuiOutlinedInput-notchedOutline': { borderColor: ui.pink },
    '&.Mui-disabled': { backgroundColor: '#FBF9FB' },
}

const FilterSelect: React.FC<Props> = ({
    label,
    placeholder,
    value,
    options = [],
    disabled = false,
    onChange,
}) => {
    const { locale } = useIntl()

    const titleOf = (option: Option) =>
        option.name ||
        option[`title${capitalize(locale)}`] ||
        option.title ||
        [option.firstName, option.lastName].filter(Boolean).join(' ')

    return (
        <Box>
            <Box sx={filterLabel}>{label}</Box>

            <Select
                size="small"
                displayEmpty
                disabled={disabled}
                value={value ?? ''}
                onChange={(e) => onChange(String(e.target.value))}
                sx={filterField}
                renderValue={(selected) => {
                    const option = options.find((item) => item.id === selected)

                    return option ? (
                        titleOf(option)
                    ) : (
                        <Box component="span" sx={{ color: ui.muted, fontWeight: 400 }}>
                            {placeholder}
                        </Box>
                    )
                }}
            >
                {options.map((option) => (
                    <MenuItem key={option.id} value={option.id}>
                        {titleOf(option)}
                    </MenuItem>
                ))}
            </Select>
        </Box>
    )
}

export default FilterSelect
